<template>
  <PropertyOnboardingStep
    :wide="true"
    :is-next-button-disabled="!description || isGeneratingDescription"
    :next-button-text="'Next'"
    :full-width="true"
    :submit="submit"
  >
    <div class="heading">
      <Transition name="heading">
        <Text :key="heading" variant="h2" align="center">{{ heading }}</Text>
      </Transition>
    </div>

    <PropertyDescriptionForm ref="formRef" />
  </PropertyOnboardingStep>
</template>

<script setup lang="ts">
import { until } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

import { Text } from '@tallo/design-system';
import { PropertyDescriptionForm, usePropertyStore } from '@tallo/investor/property';

import PropertyOnboardingStep from '../property-onboarding-step.vue';

const propertyStore = usePropertyStore();
const { isLoading } = storeToRefs(propertyStore);
const formRef = ref<InstanceType<typeof PropertyDescriptionForm>>();
const description = computed(() => formRef.value?.form.fields.description);
const isGeneratingDescription = computed(() => formRef.value?.isGeneratingDescription);
// The step renders a frame before the first draft starts; without this its heading flashes "Here is" over an empty box
const isAboutToWrite = ref(true);
const heading = computed(() =>
  isGeneratingDescription.value || (isAboutToWrite.value && !propertyStore.property?.description)
    ? 'Writing your property description'
    : 'Here is your property description',
);

async function submit() {
  await formRef.value?.save();
}

(async function init() {
  await until(isLoading).toBe(false);

  if (!description.value) {
    formRef.value?.generateDescription();
  }

  isAboutToWrite.value = false;
})();
</script>

<style scoped lang="scss">
// Both headings share one cell while they cross-fade, so the form below never moves
.heading {
  display: grid;
  margin-bottom: var(--t-spacing-lg);

  > * {
    grid-area: 1 / 1;
  }
}

.heading-enter-active,
.heading-leave-active {
  transition: opacity 200ms var(--t-ease-out);
}

.heading-enter-from,
.heading-leave-to {
  opacity: 0;
}
</style>
